import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import Tooltip from '../../../../components/Tooltip';
import { InfoNueva } from '../../../../components/MarcaBeta';
import { keyframes } from '@emotion/react';
import { endOfDay, startOfDay } from 'date-fns';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useRef, useState } from 'react';
import 'react-calendar/dist/Calendar.css';

import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { FiPlusSquare } from 'react-icons/fi';

import firebaseFirestore from '~/config/firebase/firestore';

import Locations from '../../../shared/components/Locations';

import { useSearchParams } from 'react-router-dom';
import { useAuth } from '~/hooks/useAuth';
import CalendarPicker from '~/components/Date/Calendar';
import { Reservation } from '~/core/types';
import { format, utcToZonedTime } from 'date-fns-tz';
import { EditIcon } from '@chakra-ui/icons';
import { FiCalendar, FiCamera, FiClock, FiRepeat } from 'react-icons/fi';
import { Forms } from '~/pages/public/Forms/Forms';
import useFormFields from '~/hooks/useFormFields';
import HistorialReserva from './HistorialReserva';
import TarjetaReserva from './TarjetaReserva';
import CalendarioReservas, { HuecoLibre } from './calendario/CalendarioReservas';
import RepetirReserva from './recurrencia/RepetirReserva';
import DetalleSerie from './recurrencia/DetalleSerie';
import { BotonConfirmarVista, PopOverChangeStatus } from './estado/CambiarEstado';
import { formatoPesos } from '~/utils/dinero';

type DateRange = {
  startDate: Date;
  endDate: Date;
};

/** Se recuerda por equipo, no por usuario: es un aviso, no un dato. */
const CALENDARIO_VISTO = 'sontickets:calendario-visto';

/** Un halo azul que se abre y se apaga; ni parpadea ni salta. */
const latidoCalendario = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(49, 130, 206, 0.45); }
  70%  { box-shadow: 0 0 0 8px rgba(49, 130, 206, 0); }
  100% { box-shadow: 0 0 0 0 rgba(49, 130, 206, 0); }
`;

const Reservations = () => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>();
  const [locationsId, setLocationsId] = useState<string[]>([]);
  const [, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const companyRef = useRef<any>();
  const onSnaphotRef = useRef<any>();

  const [fields] = useFormFields(user.company?.id ?? '');

  const { isOpen, onOpen, onClose } = useDisclosure();
  // Historial de una reserva concreta
  const historial = useDisclosure();
  // Vista de calendario por día, semana y mes
  const calendario = useDisclosure();

  /**
   * El calendario es nuevo y no está donde la gente lo busca, así que el botón
   * late suavemente hasta que alguien lo abre por primera vez en este equipo.
   * A partir de ahí se queda quieto: un aviso que no para de moverse termina
   * siendo ruido. La etiqueta "Nuevo" sí se queda, como en el menú de arriba.
   */
  const [calendarioSinEstrenar, setCalendarioSinEstrenar] = useState(() => {
    try {
      return localStorage.getItem(CALENDARIO_VISTO) !== '1';
    } catch {
      return true;
    }
  });

  const abrirCalendario = () => {
    setCalendarioSinEstrenar(false);
    try {
      localStorage.setItem(CALENDARIO_VISTO, '1');
    } catch {
      /* modo incógnito o cookies bloqueadas: se pierde el recuerdo, nada más */
    }
    calendario.onOpen();
  };
  const [reservaDelHistorial, setReservaDelHistorial] = useState<any>();
  // La tarjeta de confirmación, para volver a verla o capturarla
  const tarjeta = useDisclosure();
  const [reservaDeLaTarjeta, setReservaDeLaTarjeta] = useState<any>();
  // Reserva que se va a repetir en varias fechas
  const repeticion = useDisclosure();
  const [reservaARepetir, setReservaARepetir] = useState<any>();
  // Fechas de una serie ya creada, solo para mirarlas
  const detalleSerie = useDisclosure();
  const [reservaDeLaSerie, setReservaDeLaSerie] = useState<any>();
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
  });

  /**
   * El formulario avisa cuando se guarda una reserva marcada como "se repite":
   * se busca recién creada y se abre la ventana de repetición con ella delante.
   * Va por evento porque el formulario vive tres niveles por debajo del panel.
   */
  useEffect(() => {
    const alCrearRecurrente = async (evento: Event) => {
      const id = (evento as CustomEvent).detail?.reservationId;
      const codigo = (evento as CustomEvent).detail?.codigoPendiente;
      if (!id) return;
      const encontrada = await getDoc(doc(firebaseFirestore, 'reservations', id));
      if (!encontrada.exists()) return;
      onClose();
      setReservaARepetir({ id: encontrada.id, ...encontrada.data(), __codigoPendiente: codigo });
      repeticion.onOpen();
    };
    window.addEventListener('sontickets:repetir-reserva', alCrearRecurrente);
    return () => window.removeEventListener('sontickets:repetir-reserva', alCrearRecurrente);
  }, []);

  /**
   * Del calendario al formulario: se cierra el calendario y se abre la reserva
   * con la sala, el día, la hora y las personas ya puestas. Viajan por la URL
   * porque es lo que el formulario ya sabe leer; no hace falta tocarlo.
   */
  const abrirFormularioConHueco = (hueco: HuecoLibre) => {
    if (!hueco.personas) return;
    calendario.onClose();
    setSearchParams({
      from: 'sistema',
      company: user.company?.id ?? '',
      externalId: user.company?.externalId ?? '',
      lang: 'es',
      location: hueco.espacio.id,
      date: format(hueco.fecha, 'yyyy-MM-dd'),
      numberPeople: String(hueco.personas),
      // Las zonas de día completo llegan sin hora: la elige quien reserva.
      ...(hueco.horaInicio ? { startHour: hueco.horaInicio } : {}),
      ...(hueco.horaFin ? { endHour: hueco.horaFin } : {}),
    });
    onOpen();
  };

  const handleNewReservation = () => {
    setSearchParams({
      from: 'sistema',
      company: user.company?.id ?? '',
      externalId: user.company?.externalId ?? '',
      lang: 'es',
    });
    onOpen();
  };

  const handleEditReservation = (code: string) => {
    setSearchParams({
      from: 'sistema',
      company: user.company?.id ?? '',
      externalId: user.company?.externalId ?? '',
      lang: 'es',
      code,
    });
    setTimeout(() => {
      onOpen();
    }, 200);
  };

  const getReservations = async () => {
    setIsLoading(true);
    const reservationsRef = collection(firebaseFirestore, 'reservations');

    const queryConstraints = [
      orderBy('datetime', 'asc'),
      where('location.company', '==', companyRef.current),
    ];
    if (locationsId.length > 0) {
      for (const locationId of locationsId) {
        if (locationId) {
          queryConstraints.push(where('location.id', '==', locationId));
        }
      }
    }
    if (dateRange) {
      queryConstraints.push(
        where('datetime', '>=', Timestamp.fromDate(dateRange.startDate)),
        where('datetime', '<=', Timestamp.fromDate(dateRange.endDate))
      );
    }

    const q = query(reservationsRef, ...queryConstraints);

    if (onSnaphotRef.current) {
      onSnaphotRef.current.unsubscribe();
    }

    // Un location-manager solo puede ver sus sedes. Cuando elige "Todas" no
    // se filtra en la consulta (evita depender de índices compuestos), sino
    // aquí, sobre el resultado.
    const allowedLocationIds =
      user.role === 'location-manager'
        ? (user.locations ?? []).map((location) => location.id)
        : null;

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      let reservations: any[] = [];
      querySnapshot.forEach((doc) => {
        reservations.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      if (allowedLocationIds) {
        reservations = reservations.filter((reservation) =>
          allowedLocationIds.includes(reservation.location?.id)
        );
      }

      setReservations(reservations as Reservation[]);
      setIsLoading(false);
    });

    onSnaphotRef.current = {
      unsubscribe,
    };
  };

  const total = useMemo(() => {
    return reservations
      .map((res) => res.payment?.amount ?? 0)
      .reduce((acc, total) => {
        return acc + parseFloat(total.toString());
      }, 0);
  }, [reservations]);

  const numberPeopleCheckin = useMemo(() => {
    return reservations
      .filter((res) => res.status === 'checkin' || res.status === 'checkout')
      .map((res) => res.numberPeople)
      .reduce((a, b) => a + b, 0);
  }, [reservations]);

  useEffect(() => {
    companyRef.current = doc(firebaseFirestore, 'companies', user.company?.id ?? '');
  }, [user]);

  useEffect(() => {
    getReservations();
  }, [locationsId, dateRange]);

  return (
    <Flex flexDirection={{ sm: 'column', md: 'row' }}>
      <Box
        pt={5}
        flexDirection={'column'}
        justifyContent={'flex-start'}
        alignItems={'flex-start'}
        width={'full'}
        height={'100%'}
        pl={5}
        backgroundColor={'white'}
        position={{
          sm: 'relative',
          md: 'fixed',
        }}
      >
        <Grid
          w={'full'}
          h='calc(100vh - 73px)'
          templateRows='repeat(2, 1fr)'
          templateColumns='repeat(6, 1fr)'
        >
          <GridItem
            rowSpan={{
              sm: 1,
              md: 2,
            }}
            colSpan={{ sm: 5, md: 1 }}
          >
            <Locations
              companyUid={user.company?.id}
              onChange={(id) => {
                setLocationsId([id]);
              }}
            />
            <Box mt={10} />
            <CalendarPicker
              onChange={(date) => {
                setDateRange({
                  startDate: startOfDay(date),
                  endDate: endOfDay(date),
                });
              }}
            />
          </GridItem>

          <GridItem
            rowSpan={{
              sm: 30,
              md: 2,
            }}
            colSpan={{
              sm: 5,
              md: 5,
            }}
            justifyContent={'flex-start'}
          >
            <VStack>
              <HStack pt={5} bg={'white'} display={'flex'} width={'full'}>
                <HStack display={'flex'} flex={1}>
                  <Button
                    rightIcon={<FiPlusSquare />}
                    colorScheme='orange'
                    variant='outline'
                    size={'sm'}
                    onClick={handleNewReservation}
                  >
                    Nueva reserva
                  </Button>

                  <Button
                    leftIcon={<FiCalendar />}
                    colorScheme='blue'
                    variant='outline'
                    size={'sm'}
                    onClick={abrirCalendario}
                    animation={
                      calendarioSinEstrenar ? `${latidoCalendario} 2.4s ease-out infinite` : undefined
                    }
                  >
                    Ver calendario
                    <Badge ml='2' fontSize='0.8em' colorScheme='green'>
                      Nuevo
                    </Badge>
                  </Button>
                  {/* La "i" va FUERA del botón: dentro sería un elemento
                      enfocable dentro de otro, y el tabulador se quedaría
                      atrapado en medio. Pegada al borde se lee igual. */}
                  <Box ml={-1}>
                    <InfoNueva />
                  </Box>
                </HStack>

                <HStack display={'flex'} flex={1} spacing={20} pr={10}>
                  {user.role == 'admin' && (
                    <Stat width={'120px'}>
                      <StatLabel>Valor vendido </StatLabel>
                      <StatNumber data-testid='valor-vendido'>{formatoPesos(total)}</StatNumber>
                    </Stat>
                  )}
                  <Stat width={'100px'}>
                    <StatLabel># personas </StatLabel>
                    <StatNumber>
                      <HStack spacing={0}>
                        <Text size={'xs'}>{numberPeopleCheckin}</Text>
                        <Text mx={2} size={'xs'}>
                          /
                        </Text>

                        <Text size={'xs'}>
                          {reservations
                            .map((reservation) => reservation.numberPeople)

                            .reduce(
                              (previousValue, currentValue) => previousValue + currentValue,
                              0
                            )}
                        </Text>
                      </HStack>
                    </StatNumber>
                  </Stat>

                  <Stat width={'200px'}>
                    <StatLabel>#reservas</StatLabel>
                    <StatNumber>
                      <HStack spacing={0}>
                        <Text size={'xs'}>
                          {
                            reservations.filter(
                              (res) => res.status == 'checkin' || res.status == 'checkout'
                            ).length
                          }
                        </Text>
                        <Text mx={2} size={'xs'}>
                          /
                        </Text>

                        <Text size={'xs'}>{reservations.length}</Text>
                      </HStack>
                    </StatNumber>
                  </Stat>
                </HStack>
              </HStack>

              <TableContainer overflowY={'scroll'} h='calc(100vh - 200px)'>
                {isLoading && <Progress size='xs' isIndeterminate />}
                <Table overflow={'visible'} size='sm' variant='striped'>
                  <Thead>
                    <Tr>
                      <Th>Acción</Th>
                      <Th>Número de personas</Th>
                      <Th>Ubicación</Th>
                      <Th>Fecha reserva</Th>
                      <Th>Hora inicio</Th>
                      <Th>Hora Fin</Th>
                      {fields.map((field) => {
                        return <Th key={field?.slug ?? ''}>{field.name.es}</Th>;
                      })}

                      <Th>Generada desde</Th>
                      {user?.company?.others?.isSourceActive && <Th>Fuente</Th>}
                      <Th>Promociones especiales</Th>
                      <Th>Fecha solicitud</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {reservations?.map((reservation: any, index) => {
                      const date = utcToZonedTime(
                        (reservation?.datetime as Timestamp).toDate(),
                        'America/Bogota'
                      );
                      return (
                        <Tr key={`${reservation.identification}-${index}`}>
                          <Td>
                            <HStack alignItems={'flex-start'} spacing={2}>
                              {(reservation.status === 'confirmed' ||
                                reservation.status === 'checkin' ||
                                reservation.status === 'checkout') && (
                                <PopOverChangeStatus
                                  reserva={reservation}
                                  reservationId={reservation.id}
                                  datetime={reservation.datetime as Timestamp}
                                  name={reservation.name}
                                  status={reservation.status}
                                  statusHistory={reservation.statusHistory ?? []}
                                  numberPeople={reservation.numberPeople}
                                  payment={reservation.payment}
                                  onChanged={() => {
                                    getReservations();
                                  }}
                                />
                              )}

                              {(reservation.status === 'pending' || !reservation.status) && (
                                <BotonConfirmarVista reserva={reservation} />
                              )}

                              {reservation.status === 'cancelled' && (
                                <Tag size={'sm'} variant='solid' colorScheme='red'>
                                  Cancelada
                                </Tag>
                              )}
                              {reservation.status !== 'cancelled' && (
                                <IconButton
                                  onClick={() => {
                                    handleEditReservation(reservation.code);
                                  }}
                                  size={'xs'}
                                  bgColor={'transparent'}
                                  icon={<EditIcon />}
                                  aria-label={'editar reserva'}
                                />
                              )}

                              {/* Solo aparece si la reserva forma parte de una
                                  serie, y sirve para ver sus fechas. Las series
                                  se crean al reservar, marcando la casilla del
                                  formulario. */}
                              {(reservation as any).recurrence?.id && (
                                <Tooltip
                                  label={
                                    (reservation as any).recurrence?.descripcion ??
                                    'Ver las fechas de esta serie'
                                  }
                                  openDelay={400}
                                >
                                  <IconButton
                                    onClick={() => {
                                      setReservaDeLaSerie(reservation);
                                      detalleSerie.onOpen();
                                    }}
                                    size={'xs'}
                                    bgColor={'transparent'}
                                    color={'blue.500'}
                                    _hover={{ color: 'blue.600', bgColor: 'blue.50' }}
                                    icon={<FiRepeat />}
                                    aria-label={'ver las fechas de la serie'}
                                  />
                                </Tooltip>
                              )}

                              {/* Quién la hizo, quién la cambió y desde dónde.
                                  Disponible también en las canceladas: es justo
                                  cuando más falta hace saberlo. */}
                              <Tooltip label='Ver historial' openDelay={400}>
                                <IconButton
                                  onClick={() => {
                                    setReservaDelHistorial(reservation);
                                    historial.onOpen();
                                  }}
                                  size={'xs'}
                                  bgColor={'transparent'}
                                  color={'gray.500'}
                                  _hover={{ color: 'blue.500', bgColor: 'blue.50' }}
                                  icon={<FiClock />}
                                  aria-label={'ver historial de la reserva'}
                                />
                              </Tooltip>

                              {/* La tarjeta que vio el cliente al reservar, para
                                  hacerle captura y mandársela. No en las
                                  canceladas: diría "confirmada". */}
                              {reservation.status !== 'cancelled' && (
                                <Tooltip
                                  label='Tarjeta de confirmación (para captura de pantalla)'
                                  openDelay={400}
                                >
                                  <IconButton
                                    onClick={() => {
                                      setReservaDeLaTarjeta(reservation);
                                      tarjeta.onOpen();
                                    }}
                                    size={'xs'}
                                    bgColor={'transparent'}
                                    color={'gray.500'}
                                    _hover={{ color: 'blue.500', bgColor: 'blue.50' }}
                                    icon={<FiCamera />}
                                    aria-label={'ver tarjeta de confirmación'}
                                  />
                                </Tooltip>
                              )}
                            </HStack>
                          </Td>

                          <Td>{reservation.numberPeople}</Td>
                          <Td>{reservation?.location?.name ?? ''}</Td>
                          <Td>{format(date, 'dd/MM/yyyy')}</Td>
                          <Td>
                            {reservation?.startHour ? reservation.startHour : reservation.time}
                          </Td>
                          <Td>{reservation.endHour ?? 'No Aplica'}</Td>
                          {fields.map((field) => {
                            if (typeof reservation[field?.slug ?? ''] === 'boolean') {
                              return (
                                <Td key={field.slug}>
                                  {reservation[field?.slug ?? ''] ? 'si' : 'no'}
                                </Td>
                              );
                            }
                            return <Td key={field.slug}>{reservation[field?.slug ?? '']}</Td>;
                          })}
                          <Td>{reservation.from ?? '--'}</Td>
                          {user.company?.others?.isSourceActive && (
                            <Td>{reservation.source ?? '--'}</Td>
                          )}
                          <Td>{reservation.acceptReceiveNews ? 'si' : 'no'}</Td>
                          <Td>{format(new Date(reservation.createdAt), 'dd/MM/yyyy hh:mm a')}</Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </VStack>
          </GridItem>
        </Grid>
      </Box>
      {reservaARepetir && (
        <RepetirReserva
          isOpen={repeticion.isOpen}
          onClose={() => {
            repeticion.onClose();
            setReservaARepetir(undefined);
          }}
          reserva={reservaARepetir}
          codigoPendiente={reservaARepetir.__codigoPendiente}
          onCreadas={getReservations}
        />
      )}

      {reservaDeLaSerie && (
        <DetalleSerie
          isOpen={detalleSerie.isOpen}
          onClose={() => {
            detalleSerie.onClose();
            setReservaDeLaSerie(undefined);
          }}
          reserva={reservaDeLaSerie}
        />
      )}

      <CalendarioReservas
        isOpen={calendario.isOpen}
        onClose={calendario.onClose}
        onHuecoElegido={abrirFormularioConHueco}
        companyId={user.company?.id}
        // Se abre siempre en el día que muestra la tabla.
        fechaInicial={dateRange?.startDate}
      />

      <HistorialReserva
        reserva={reservaDelHistorial}
        isOpen={historial.isOpen}
        onClose={historial.onClose}
      />

      <TarjetaReserva
        reserva={reservaDeLaTarjeta}
        isOpen={tarjeta.isOpen}
        onClose={tarjeta.onClose}
      />

      <Modal onClose={onClose} size={'full'} isOpen={isOpen}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Generar reserva</ModalHeader>
          <ModalCloseButton size={'lg'} />
          <ModalBody>
            <Forms />
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default Reservations;
